import { Injectable } from '@angular/core';
import { SeoConfig } from '@main/schema-org/const';
import { CustomProduct } from '@product/const';
import { ProductReviewsService } from '@reviews/data-access';
import { JsonLdBuilder } from '@spartacus/storefront';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

/**
 * Builds the structured data for the product reviews, see https://schema.org/Review.
 * The data includes the aggregated product rating and the individual reviews.
 */
@Injectable({
  providedIn: 'root',
})
export class CustomJsonLdProductReviewBuilder implements JsonLdBuilder<CustomProduct> {
  constructor(
    protected reviewsService: ProductReviewsService,
    protected config: SeoConfig,
  ) {}

  build(product: CustomProduct): Observable<any> {
    return combineLatest([this.buildAggregateRating(product), this.buildReview(product)]).pipe(
      map(([aggregateRating, review]) => ({ ...aggregateRating, ...review })),
    );
  }

  private buildAggregateRating(product: CustomProduct): Observable<any> {
    return combineLatest([
      this.reviewsService.getAverageRating(product.code),
      this.reviewsService.getTotalCount(product.code),
    ]).pipe(
      map(([rating, count]) =>
        count
          ? {
              aggregateRating: {
                '@type': 'AggregateRating',
                ratingValue: rating,
                reviewCount: count,
                worstRating: 0,
              },
            }
          : {},
      ),
    );
  }

  private buildReview(product: CustomProduct): Observable<any> {
    return this.reviewsService.getReviews(product.code).pipe(
      map((reviewsInfo) => reviewsInfo.value?.reviews || []),
      map((reviews) =>
        reviews.map((review) => ({
          '@type': 'Review',
          author: {
            '@type': 'Person',
            name: review.authorName,
          },
          reviewRating: {
            '@type': 'Rating',
            ratingValue: review.rate,
            worstRating: 0,
          },
          date: review.date,
          ...(review.pros && { positiveNotes: review.pros }),
          ...(review.cons && { negativeNotes: review.cons }),
          ...(review.stringReviewText && { reviewBody: review.stringReviewText }),
        })),
      ),
      map((review) => (review.length > 0 ? { review } : {})),
    );
  }
}
